/**
 * Profile Questionnaire — effects.
 *
 * Side effects live here. The reducer never calls these directly;
 * `useProfileQuestionnaireProgram.ts` runs them in a `useEffect`.
 *
 * Scope:
 *   - `LoadExistingProfile`: GET /api/v1/{friend|sponsor}/profile
 *     (now also reads the `photos` field per B-2.B and dispatches
 *     them into the persisted photo state).
 *   - `SaveProfile`: PUT /api/v1/{friend|sponsor}/profile with the
 *     canonical payload derived from `formToUpdatePayload`.
 *   - `RevokePhotoUrl`: legacy cleanup for `URL.createObjectURL`
 *     references (kept for backward compatibility with prior tests).
 *   - `UploadPhoto` / `DeletePhoto`: B-2.B photo lifecycle.
 *   - `CreateObjectURLPreview` / `RevokeObjectURLPreview`:
 *     browser-port seam for `URL.createObjectURL` lifecycle.
 */

import {
  createOrUpdateFriendProfile,
  getFriendProfile,
  createOrUpdateSponsorProfile,
  getSponsorProfile,
} from '@/api/profiles';
import {
  uploadFriendProfilePhoto,
  uploadSponsorProfilePhoto,
  deleteFriendProfilePhoto,
  deleteSponsorProfilePhoto,
  normalizeProfilePhotosField,
  type ProfilePhoto,
} from '@/api/profilePhotos';
import {
  listSkillsRaw,
  getFriendProfileSkillsRaw,
  getSponsorProfileSkillsRaw,
  setFriendProfileSkillsRaw,
  setSponsorProfileSkillsRaw,
  normalizeProfileSkillsResponse,
  normalizeSkillsResponse,
  SkillsError,
} from '@/api/skills';
import type { PhotoEntry, ProfileForm } from './model';
import { profileToForm, formToUpdatePayload } from './update';
import type { ProfileQuestionnaireEffect, UpdateResult } from './update';
import type { ProfileQuestionnaireMsg } from './messages';
import type { RegistrationKind } from '@/store/authStore';
import {
  defaultObjectUrlPort,
  type ObjectUrlPort,
} from '@/api/objectUrlPort';

export interface EffectContext {
  readonly dispatch: (msg: ProfileQuestionnaireMsg) => void;
  readonly registrationKind: RegistrationKind;
  /**
   * Browser-port seam for `URL.createObjectURL` / `revokeObjectURL`.
   * Defaults to the real browser implementation; tests may inject a
   * recording port to verify create / revoke parity without mocking
   * business logic.
   */
  readonly objectUrlPort?: ObjectUrlPort;
}

/**
 * Run a single effect. Returns a cleanup function if any.
 */
export async function runEffect(
  effect: ProfileQuestionnaireEffect,
  context: EffectContext
): Promise<(() => void) | void> {

  const port = context.objectUrlPort ?? defaultObjectUrlPort;
  switch (effect.type) {
    case 'LoadExistingProfile':
      return runLoadExisting(context);
    case 'SaveProfile':
      return runSaveProfile(context, effect.form, effect.kind);
    case 'ScheduleCompletionModalDismiss':
      // Modal is dismiss-only today (no auto-dismiss per §36). Reserved.
      return;
    case 'RevokePhotoUrl':
      port.revokeObjectURL(effect.url);
      return;
    case 'RevokeObjectURLPreview':
      port.revokeObjectURL(effect.url);
      return;
    case 'UploadPhoto':
      return runUploadPhoto(context, effect.localId, effect.file);
    case 'DeletePhoto':
      return runDeletePhoto(context, effect.photoID);
    case 'LoadSkillsTaxonomy':
      return runLoadSkillsTaxonomy(context);
    case 'LoadProfileSkills':
      return runLoadProfileSkills(context);
    case 'SaveProfileSkills':
      return runSaveProfileSkills(context, effect.skillIds);
    default: {
      // Exhaustive: every union variant handled above.
      return;
    }
  }
}

async function runLoadExisting(context: EffectContext): Promise<void> {
  try {
    if (context.registrationKind === 'friend') {
      const profile = await getFriendProfile();
      if (!profile) {
        context.dispatch({
          type: 'ExistingProfileLoaded',
          profile: {},
          persistedPhotos: [],
          profileStatus: 'inactive',
          missingActivationFields: [],
        });
        return;
      }
      const { photos: persistedPhotos, violations: photoViolations } =
        photosFromProfile(profile.photos);
      context.dispatch({
        type: 'ExistingProfileLoaded',
        profile: profileToForm(profile).form,
        persistedPhotos,
        profileStatus: profile.profile_status,
        missingActivationFields: profile.missing_activation_fields ?? [],
        formProtocolViolations: profileToForm(profile).protocolViolations,
        photoProtocolViolations: photoViolations,
      });
      return;
    }
    if (context.registrationKind === 'sponsor') {
      const profile = await getSponsorProfile();
      if (!profile) {
        context.dispatch({
          type: 'ExistingProfileLoaded',
          profile: {},
          persistedPhotos: [],
          profileStatus: 'inactive',
          missingActivationFields: [],
        });
        return;
      }
      const { photos: persistedPhotos, violations: photoViolations } =
        photosFromProfile(profile.photos);
      context.dispatch({
        type: 'ExistingProfileLoaded',
        profile: profileToForm(profile).form,
        persistedPhotos,
        profileStatus: profile.profile_status,
        missingActivationFields: profile.missing_activation_fields ?? [],
        formProtocolViolations: profileToForm(profile).protocolViolations,
        photoProtocolViolations: photoViolations,
      });
      return;
    }
    // Unknown registration kind — fail closed.
    context.dispatch({
      type: 'ExistingProfileLoadFailed',
      error: 'Не удалось загрузить профиль',
    });
  } catch {
    context.dispatch({
      type: 'ExistingProfileLoadFailed',
      error: 'Не удалось загрузить профиль',
    });
  }
}

/**
 * Convert a server-returned photo list into PhotoEntry[] persisted
 * entries.
 *
 * The single source of truth for normalization is
 * `normalizeProfilePhotosField`. We rely on it here; the reducer
 * uses the result verbatim.
 *
 * CORRECTION01 R4: returns both the valid persisted entries AND
 * any protocol violations. The reducer surfaces violations via
 * `serverError` so contract drift is loud, not silent.
 */
function photosFromProfile(
  rawPhotos: readonly ProfilePhoto[] | undefined
): { readonly photos: PhotoEntry[]; readonly violations: readonly string[] } {
  const normalized = normalizeProfilePhotosField(rawPhotos);
  const photos: PhotoEntry[] = normalized.photos.map((p) => ({
    id: p.id,
    kind: 'persisted' as const,
    url: p.url,
    position: p.position,
  }));
  return { photos, violations: normalized.violations };
}

async function runSaveProfile(
  context: EffectContext,
  form: ProfileForm,
  kind: 'create' | 'edit'
): Promise<void> {
  void kind;
  try {
    if (context.registrationKind === 'friend') {
      const payload = formToUpdatePayload(form, 'friend');
      const saved = await createOrUpdateFriendProfile({
        ...payload,
        profile_type: 'friend',
      });
      context.dispatch({
        type: 'SubmitSucceeded',
        profile: profileToForm(saved).form,
        profileStatus: saved.profile_status,
        missingActivationFields: saved.missing_activation_fields ?? [],
        formProtocolViolations: profileToForm(saved).protocolViolations,
      });
      return;
    }
    if (context.registrationKind === 'sponsor') {
      const payload = formToUpdatePayload(form, 'sponsor');
      const saved = await createOrUpdateSponsorProfile({
        ...payload,
        profile_type: 'sponsor',
      });
      context.dispatch({
        type: 'SubmitSucceeded',
        profile: profileToForm(saved).form,
        profileStatus: saved.profile_status,
        missingActivationFields: saved.missing_activation_fields ?? [],
        formProtocolViolations: profileToForm(saved).protocolViolations,
      });
      return;
    }
    // ACT-INDEEP-FRIEND-SCENARIO-CLASSIFICATION-AND-FIRST-SAVE01
    // CORRECTION01 (Hub2 discrepancy audit): the Friend scenario
    // surface is `no_work` ONLY. The `equal` scenario ("Я богат/богата,
    // ищу такого же" → "Равный партнёр") is an INDEPENDENT third role
    // per docs/specs/indeep_requirements_spec_v2.md REQ-006
    // (men: "Ищу равную"; women: "Ищу равного") and must NOT be
    // collapsed into Friend. See CORRECTION01 §C1.
    //
    // Friend first-save path:
    //   - unclassified + scenario='no_work' → POST /api/v1/friend/profile
    //     The server performs the `unclassified → friend`
    //     classification followed by the profile create as two
    //     separately committed writes with a bounded compensating
    //     revert on profile-persistence failure
    //     (CORRECTION01 §C3 + CORRECTION02 §C10). This is NOT
    //     PostgreSQL transactional atomicity — see
    //     ACT-INDEEP-FRIEND-PROFILE-ATOMIC-TX01 for the deferred
    //     BEGIN/COMMIT boundary that replaces this compensation.
    //     The frontend never sets registration_kind.
    //
    // Out of scope for THIS ACT (explicit fail-closed):
    //   - unclassified + scenario='equal'   → Equal Partner role;
    //     registration_kind='equal' is not yet implemented in the
    //     canonical system (lifecycle contract P0-A never landed;
    //     see ACT-INDEEP-EQUAL-PARTNER-ROLE-REQUIREMENT-RESTORATION01
    //     follow-up). We emit the explicit
    //     `equal_role_not_implemented` discriminator so the view can
    //     render a clear, scenario-specific message rather than
    //     silently falling through to friend or sponsor.
    //   - unclassified + scenario='sponsor' → sponsor onboarding
    //     path; out of this ACT's scope.
    //   - unclassified + scenario=undefined → no scenario selected;
    //     the existing P0-C `role_not_classified` discriminator
    //     handles this.
    //
    // OWASP Authorization Cheat Sheet: the server is the sole
    // authority for the registration_kind transition. We only REQUEST
    // the friend transition by selecting the friend endpoint.
    if (context.registrationKind === 'unclassified') {
      const scenario = form.scenario;
      if (scenario === 'no_work') {
        const payload = formToUpdatePayload(form, 'friend');
        const saved = await createOrUpdateFriendProfile({
          ...payload,
          profile_type: 'friend',
        });
        context.dispatch({
          type: 'SubmitSucceeded',
          profile: profileToForm(saved).form,
          profileStatus: saved.profile_status,
          missingActivationFields: saved.missing_activation_fields ?? [],
          formProtocolViolations: profileToForm(saved).protocolViolations,
        });
        return;
      }
      // scenario === 'equal': Hub2 audit correction. Equal is a
      // distinct role per docs/specs/indeep_requirements_spec_v2.md
      // REQ-006 ("Ищу равного"/"Ищу равную"). The canonical role
      // model has not yet been extended to admit registration_kind
      // = 'equal'. Emit the explicit not-yet-implemented
      // discriminator. CORRECTION01 §C1.
      if (scenario === 'equal') {
        context.dispatch({
          type: 'SubmitFailed',
          code: 'equal_role_not_implemented',
          error:
            'Роль «Равный партнёр» ещё не подключена. Выберите «Я не хочу работать» или завершите регистрацию.',
        });
        return;
      }
      // scenario === 'sponsor' or undefined: out of scope for this
      // ACT. Fall through to the explicit role_not_classified
      // dispatch below.
    }
    // Unclassified principal with no friend scenario selected (or
    // with the sponsor scenario). The frontend does NOT silently
    // classify the account; the user must make an explicit Friend
    // scenario choice (scenarios A or C per partner-corrections
    // §1) before the save can route to the friend endpoint.
    //
    // P0-C discriminator preserved verbatim: emit a specific code
    // so the view can render a clear "complete onboarding first"
    // message rather than the generic Russian error string. We do
    // NOT call fetch() in this branch — that is the very bug P0-C
    // fixed. The unclassified-without-Friend-scenario flow is
    // routed to /profile-completion, not /profile-questionnaire.
    context.dispatch({
      type: 'SubmitFailed',
      code: 'role_not_classified',
      error:
        'Завершите регистрацию, чтобы сохранить анкету.',
    });
  } catch {
    context.dispatch({
      type: 'SubmitFailed',
      code: 'network_error',
      error: 'Не удалось сохранить профиль',
    });
  }
}

async function runUploadPhoto(
  context: EffectContext,
  localId: string,
  file: File
): Promise<void> {
  // CORRECTION01 R2: do NOT dispatch PhotoUploadStarted here.
  // The reducer that emitted this UploadPhoto effect already
  // owns the kind transition:
  //   - PhotoSelected with File → entry kind='new-local'; effect
  //     emitted; we run the adapter here (kind transitions to
  //     'uploading' on PhotoUploadSucceeded/Failed outcome).
  //   - PhotoUploadStarted (retry) → reducer flips kind to
  //     'uploading' AND emits UploadPhoto effect; we run the
  //     adapter here.
  //
  // Dispatching PhotoUploadStarted from this effect would create
  // an infinite loop because the reducer now re-emits UploadPhoto
  // when it sees a File on the entry.
  try {
    const response =
      context.registrationKind === 'friend'
        ? await uploadFriendProfilePhoto(file)
        : await uploadSponsorProfilePhoto(file);
    context.dispatch({
      type: 'PhotoUploadSucceeded',
      id: localId,
      url:
        response.photos.length > 0
          ? response.photos[response.photos.length - 1]?.url ?? ''
          : '',
      serverPhotos: response.photos,
    });
  } catch (err) {
    const message = err instanceof Error ? err.message : 'upload failed';
    context.dispatch({
      type: 'PhotoUploadFailed',
      id: localId,
      error: message,
    });
  }
}

async function runDeletePhoto(
  context: EffectContext,
  photoID: string
): Promise<void> {
  try {
    const response =
      context.registrationKind === 'friend'
        ? await deleteFriendProfilePhoto(photoID)
        : await deleteSponsorProfilePhoto(photoID);
    context.dispatch({
      type: 'PhotoDeleteSucceeded',
      id: photoID,
      serverPhotos: response.photos,
    });
  } catch (err) {
    const message = err instanceof Error ? err.message : 'delete failed';
    context.dispatch({
      type: 'PhotoDeleteFailed',
      id: photoID,
      error: message,
    });
  }
}

// =============================================================================
// B-2.C skills effects
// =============================================================================

async function runLoadSkillsTaxonomy(context: EffectContext): Promise<void> {
  try {
    const body = await listSkillsRaw();
    // CORRECTION01 R2: strict envelope normalization. Envelope-level
    // violations (N10..N16 — missing/wrong-type limit / offset /
    // has_more) are FAILURES; per-item violations (N2..N6) are
    // adopted-with-warnings because the response is structurally
    // well-formed even when individual entries are bad. The two
    // classes are separated by string prefix by the helper:
    // envelope violations start with one of the four field names
    // (`limit:`, `offset:`, `has_more:`) or with `response:` /
    // `skills: missing` / `skills: not an array`.
    const {
      skills,
      hasMore,
      violations,
    } = normalizeSkillsResponse(body);
    const envelopeViolations = violations.filter(
      (v) =>
        v.startsWith('response:') ||
        v.startsWith('limit:') ||
        v.startsWith('offset:') ||
        v.startsWith('has_more:') ||
        v === 'skills: missing' ||
        v === 'skills: not an array'
    );
    const itemViolations = violations.filter(
      (v) => !envelopeViolations.includes(v)
    );
    if (envelopeViolations.length > 0) {
      // Envelope is structurally wrong; refuse to adopt.
      context.dispatch({
        type: 'SkillsTaxonomyLoadFailed',
        error: envelopeViolations.join('; '),
      });
      return;
    }
    context.dispatch({
      type: 'SkillsTaxonomyLoaded',
      skills,
      hasMore,
      protocolViolations: itemViolations,
    });
  } catch (err) {
    const message =
      err instanceof SkillsError
        ? err.message
        : 'Не удалось загрузить список интересов';
    context.dispatch({
      type: 'SkillsTaxonomyLoadFailed',
      error: message,
    });
  }
}

async function runLoadProfileSkills(context: EffectContext): Promise<void> {
  try {
    const body =
      context.registrationKind === 'friend'
        ? await getFriendProfileSkillsRaw()
        : await getSponsorProfileSkillsRaw();
    // CORRECTION01 R2: strict envelope normalization. The `skills`
    // field is required. Per-item violations are adopted-with-warning.
    const { skills, violations } = normalizeProfileSkillsResponse(body);
    const envelopeViolations = violations.filter(
      (v) =>
        v.startsWith('response:') ||
        v === 'skills: missing' ||
        v === 'skills: not an array'
    );
    const itemViolations = violations.filter(
      (v) => !envelopeViolations.includes(v)
    );
    if (envelopeViolations.length > 0) {
      context.dispatch({
        type: 'ProfileSkillsLoadFailed',
        error: envelopeViolations.join('; '),
      });
      return;
    }
    const selectedSkillIds = skills.map((s) => s.id);
    context.dispatch({
      type: 'ProfileSkillsLoaded',
      selectedSkillIds,
      skills,
      protocolViolations: itemViolations,
    });
  } catch (err) {
    const message =
      err instanceof SkillsError
        ? err.message
        : 'Не удалось загрузить интересы';
    context.dispatch({
      type: 'ProfileSkillsLoadFailed',
      error: message,
    });
  }
}

async function runSaveProfileSkills(
  context: EffectContext,
  skillIds: readonly string[]
): Promise<void> {
  try {
    const body =
      context.registrationKind === 'friend'
        ? await setFriendProfileSkillsRaw({ skill_ids: skillIds })
        : await setSponsorProfileSkillsRaw({ skill_ids: skillIds });
    // CORRECTION01 R2: strict envelope normalization. Envelope
    // violations are save failures (the response does not include
    // the canonical post-save selection); per-item violations are
    // save failures too, because we cannot trust the canonical
    // list.
    const { skills, violations } = normalizeProfileSkillsResponse(body);
    if (violations.length > 0) {
      context.dispatch({
        type: 'ProfileSkillsSaveFailed',
        error: violations.join('; '),
        retainedSelectedIds: [...skillIds],
      });
      return;
    }
    context.dispatch({
      type: 'ProfileSkillsSaveSucceeded',
      skills,
    });
  } catch (err) {
    const message =
      err instanceof SkillsError
        ? err.message
        : 'Не удалось сохранить интересы';
    context.dispatch({
      type: 'ProfileSkillsSaveFailed',
      error: message,
      retainedSelectedIds: [...skillIds],
    });
  }
}

/** Re-export for tests. */
export type { UpdateResult };
export type PhotoEffectInput = PhotoEntry;
